import { FC } from 'react'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { ReactComponent as DiagonalArrowUp } from 'src/assets/svg/diagonalArrowUp.svg'
import { ReactComponent as DiagonalArrowDown } from 'src/assets/svg/diagonalArrowDown.svg'
import styles from './campaignComparisonChart.module.scss'

interface CampaignData {
  id: string
  mentions: number
  name: string
  sentiments?: {
    negative: number
    neutral: number
    positive: number
  }
  sources?: Record<string, number>
}

interface SentimentsTabProps {
  benchmarkCampaign: string | null
  selectedData: CampaignData[]
  stats?: any
}

const sentimentColors = {
  negative: '#FF6833', // Orange (burning-orange)
  neutral: '#C7CED1', // Gray (grey-200)
  positive: '#8080FF', // Purple (malibu)
}

export const SentimentsTab: FC<SentimentsTabProps> = ({
  selectedData,
  benchmarkCampaign,
  stats,
}) => {
  // Limit to max 9 campaigns
  const limitedData = selectedData.slice(0, 9)

  // Use API stats if available
  const totalMentions =
    stats?.totalMentions ??
    limitedData.reduce((sum, campaign) => sum + campaign.mentions, 0)

  // Calculate most positive/negative campaigns if not provided by API
  let mostPositiveCampaign = stats?.mostPositiveCampaign?.name
  let mostNegativeCampaign = stats?.mostNegativeCampaign?.name

  if (!mostPositiveCampaign || !mostNegativeCampaign) {
    let maxPositivePercentage = -1
    let maxNegativePercentage = -1

    limitedData.forEach((campaign) => {
      const sentiments = campaign.sentiments
      if (sentiments && campaign.mentions > 0) {
        const positivePercentage =
          (sentiments.positive / campaign.mentions) * 100
        const negativePercentage =
          (sentiments.negative / campaign.mentions) * 100

        if (
          !mostPositiveCampaign &&
          positivePercentage > maxPositivePercentage
        ) {
          maxPositivePercentage = positivePercentage
          mostPositiveCampaign = campaign.name
        }

        if (
          !mostNegativeCampaign &&
          negativePercentage > maxNegativePercentage
        ) {
          maxNegativePercentage = negativePercentage
          mostNegativeCampaign = campaign.name
        }
      }
    })
  }

  mostPositiveCampaign = mostPositiveCampaign || 'No data'
  mostNegativeCampaign = mostNegativeCampaign || 'No data'

  // Calculate max value for chart scaling
  const maxMentions = Math.max(...limitedData.map((c) => c.mentions), 10)
  const chartMax = Math.ceil(maxMentions * 1.2) // Add 20% padding

  // Generate Y-axis labels
  const yAxisLabels = []
  const step = Math.ceil(chartMax / 5)
  for (let i = 5; i >= 0; i--) {
    yAxisLabels.push(step * i)
  }

  return (
    <>
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <AnnouncementIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>
              {/* Only the compared campaigns, unlike the page's own Total
                  Mentions above — say so, or the two read as a contradiction. */}
              Mentions · {limitedData.length} selected
              {limitedData.length === 1 ? ' campaign' : ' campaigns'}
            </div>
            <div className={styles.statValue}>
              {totalMentions.toLocaleString()}
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Most Positive</div>
            <div className={styles.statValue}>{mostPositiveCampaign}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Most Negative</div>
            <div className={styles.statValue}>{mostNegativeCampaign}</div>
          </div>
        </div>
      </div>

      <div className={styles.chartContainer}>
        <div className={styles.chart}>
          <div className={styles.yAxis}>
            {yAxisLabels.slice(0, 5).map((label) => (
              <div className={styles.yLabel} key={label}>
                {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
              </div>
            ))}
          </div>

          <div className={styles.chartArea}>
            <div className={styles.gridLines}>
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
            </div>

            <div className={styles.bars}>
              {limitedData.map((campaign) => {
                const campaignSentiments = campaign.sentiments || {
                  negative: 0,
                  neutral: 0,
                  positive: 0,
                }
                const totalMentions = campaign.mentions || 0

                // Calculate the actual bar height in pixels (max 260px)
                const maxBarHeight = 260
                const barHeightPixels =
                  chartMax > 0 ? (totalMentions / chartMax) * maxBarHeight : 0

                // Ensure minimum height for visibility
                const actualBarHeight =
                  totalMentions > 0 ? Math.max(barHeightPixels, 20) : 0

                const sortedSentiments = [
                  ['neutral', campaignSentiments.neutral],
                  ['negative', campaignSentiments.negative],
                  ['positive', campaignSentiments.positive],
                ] as [string, number][]

                const isBenchmark = campaign.id === benchmarkCampaign

                // Calculate percentage difference from benchmark
                let percentageDiff = null
                if (benchmarkCampaign && !isBenchmark) {
                  const benchmarkData = limitedData.find(
                    (c) => c.id === benchmarkCampaign
                  )
                  if (benchmarkData && benchmarkData.mentions > 0) {
                    percentageDiff =
                      ((totalMentions - benchmarkData.mentions) /
                        benchmarkData.mentions) *
                      100
                  }
                }

                return (
                  <div className={styles.barWrapper} key={campaign.id}>
                    <div
                      className={styles.stackedBar}
                      style={{ height: `${actualBarHeight}px` }}
                    >
                      {sortedSentiments.map(([sentiment, value]) => {
                        // Calculate segment as percentage of total mentions
                        const segmentPercentage =
                          totalMentions > 0
                            ? ((value as number) / totalMentions) * 100
                            : 0

                        return (
                          <div
                            className={styles.barSegment}
                            key={sentiment}
                            style={{
                              backgroundColor:
                                sentimentColors[
                                  sentiment as keyof typeof sentimentColors
                                ],
                              height: `${segmentPercentage}%`,
                            }}
                          />
                        )
                      })}
                    </div>
                    <div className={styles.barTooltip}>
                      <div className={styles.tooltipTitle}>{campaign.name}</div>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>Positive:</span>
                        <span className={styles.tooltipValue}>
                          {campaignSentiments.positive.toLocaleString()}
                        </span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>Neutral:</span>
                        <span className={styles.tooltipValue}>
                          {campaignSentiments.neutral.toLocaleString()}
                        </span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>Negative:</span>
                        <span className={styles.tooltipValue}>
                          {campaignSentiments.negative.toLocaleString()}
                        </span>
                      </div>
                      {percentageDiff !== null && (
                        <div
                          className={`${styles.tooltipDiff} ${
                            percentageDiff >= 0
                              ? styles.positive
                              : styles.negative
                          }`}
                        >
                          {percentageDiff >= 0 ? (
                            <DiagonalArrowUp className={styles.tooltipArrow} />
                          ) : (
                            <DiagonalArrowDown
                              className={styles.tooltipArrow}
                            />
                          )}
                          {Math.abs(percentageDiff).toFixed(1)}%
                        </div>
                      )}
                      {isBenchmark && (
                        <div className={styles.tooltipBenchmark}>Benchmark</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className={styles.xAxis}>
              {limitedData.map((campaign) => {
                const isBenchmark = campaign.id === benchmarkCampaign
                return (
                  <div
                    className={styles.xLabel}
                    key={campaign.id}
                    onMouseEnter={(e) => {
                      const nameElement = e.currentTarget.querySelector(
                        `.${styles.campaignName}`
                      ) as HTMLElement
                      const tooltipElement = e.currentTarget.querySelector(
                        `.${styles.campaignNameTooltip}`
                      ) as HTMLElement
                      if (
                        nameElement &&
                        tooltipElement &&
                        nameElement.scrollWidth > nameElement.clientWidth
                      ) {
                        tooltipElement.style.opacity = '1'
                        tooltipElement.style.visibility = 'visible'
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltipElement = e.currentTarget.querySelector(
                        `.${styles.campaignNameTooltip}`
                      ) as HTMLElement
                      if (tooltipElement) {
                        tooltipElement.style.opacity = '0'
                        tooltipElement.style.visibility = 'hidden'
                      }
                    }}
                  >
                    <span className={styles.campaignName}>
                      {campaign.name}
                      {isBenchmark && (
                        <StarIcon className={styles.benchmarkIcon} />
                      )}
                    </span>
                    <div className={styles.campaignNameTooltip}>
                      {campaign.name}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className={styles.yAxisRight}>
            {yAxisLabels.slice(0, 5).map((label) => (
              <div className={styles.yLabel} key={label}>
                {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.legendContainer}>
          {[
            { color: sentimentColors.positive, name: 'Positive' },
            { color: sentimentColors.neutral, name: 'Neutral' },
            { color: sentimentColors.negative, name: 'Negative' },
          ].map(({ color, name }) => (
            <div className={styles.legendItem} key={name}>
              <div
                className={styles.legendColor}
                style={{ backgroundColor: color }}
              />
              <span>{name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
